import { Suspense, useEffect, useRef } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Page, PageBody } from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useAnalytics } from '@/services/analytics';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useCanConfigureObjects, useIsRedacted } from '@/services/permissions/hooks';
import { ObjectListTable } from './components/object-list-table';
import { ObjectTypeHeader } from './components/object-type-header';
import { ObjectTypeResolver } from './object-type-resolver';
import { summarizeObjectFilters } from '@/features/dryad';
import { useObjectColumns } from './use-object-columns';
import { queryableObjectFilters, useObjectList } from './use-object-list';
import type { ObjectType } from '@/services/db/object-type/entity';
import type { WorkspaceEnvironment } from '@/services/db/environment/entity';

type ObjectTypeResolvedProps = {
  objectType: ObjectType;
  workspaceId: number;
  environment: WorkspaceEnvironment['environment'];
  canConfigure: boolean;
  canCreate: boolean;
  canDelete: boolean;
  canExport: boolean;
  canCreateRelationships: boolean;
  canViewSettings: boolean;
  shouldRedactCreatedAt: boolean;
};

function ObjectTypeResolved(props: ObjectTypeResolvedProps) {
  const state = useObjectList(props.objectType.id);
  const columns = useObjectColumns({
    workspaceId: props.workspaceId,
    objectTypeId: props.objectType.id,
    sentAttributes: props.objectType.sent_attributes ?? [],
    isSampleWorkspace: props.environment?.is_sample_workspace === true,
  });
  const { track } = useAnalytics();
  const tracked = useRef(0);
  const filterSummary = summarizeObjectFilters(state.filters);

  useEffect(() => {
    if (tracked.current === state.query.dataUpdatedAt) return;
    tracked.current = state.query.dataUpdatedAt;
    void track('objects', 'listViewedSearchedFiltered', {
      'Filter Operator': filterSummary.operator,
      'Number of Object Attribute Conditions': filterSummary.objectAttributes,
      'Number of Relationship Attribute Conditions': filterSummary.relationshipAttributes,
      'Has Enabled Objects': true,
      'Has Objects Data': state.objects.length > 0,
      'Filtered by Name': state.name !== '',
      'Name Filter Includes Wildcard': state.name.includes('*'),
      'Filtered by Without Relationships': state.withoutRelationships,
      'Filtered by With Relationships': state.withRelationships,
    });
  }, [
    filterSummary.objectAttributes,
    filterSummary.operator,
    filterSummary.relationshipAttributes,
    state.name,
    state.objects.length,
    state.query.dataUpdatedAt,
    state.withRelationships,
    state.withoutRelationships,
    track,
  ]);

  return (
    <>
      <DocumentTitle title={props.objectType.name} />
      <Page>
        <ObjectTypeHeader
          objectType={props.objectType}
          workspaceId={props.workspaceId}
          environment={props.environment}
          activeTab="list"
          canViewSettings={props.canViewSettings}
          canCreate={props.canCreate}
          canExport={props.canExport}
          canCreateRelationships={props.canCreateRelationships}
          displayedAttributes={columns.availableColumns.filter(
            (attribute) => columns.columnVisibility[attribute] !== false,
          )}
          exportFilters={queryableObjectFilters(state.filters, props.objectType.id) ?? null}
          isExportBroaderThanList={state.name !== '' || state.withoutRelationships || state.withRelationships}
          isListLoaded={state.query.isSuccess}
        />
        <PageBody>
          <ObjectListTable
            objectType={props.objectType}
            objects={state.objects}
            total={state.total}
            page={state.page}
            name={state.name}
            filters={state.filters}
            activeFilterBy={state.activeFilterBy}
            withoutRelationships={state.withoutRelationships}
            withRelationships={state.withRelationships}
            message={state.message}
            isLoading={state.query.isFetching}
            canConfigure={props.canConfigure}
            canDelete={props.canDelete}
            shouldRedactCreatedAt={props.shouldRedactCreatedAt}
            availableColumns={columns.availableColumns}
            columnVisibility={columns.columnVisibility}
            onColumnVisibilityChange={columns.onColumnVisibilityChange}
            columnsSettings={columns.columnsSettings}
            rowSelection={state.rowSelection}
            allSelected={state.allSelected}
            allMatchingSelected={state.allMatchingSelected}
            deleteFilters={state.deleteFilters}
            onRowSelectionChange={state.setRowSelection}
            onSelectAll={state.selectAll}
            onClearSelection={state.clearSelection}
            onSearch={state.search}
            onApplyFilters={state.applyFilters}
            onActivateFilterBy={state.activateFilterBy}
            onPageChange={state.setPage}
            onSavedViewChange={state.setSavedView}
            workspaceId={props.workspaceId}
          />
        </PageBody>
      </Page>
    </>
  );
}

function ObjectTypeContent() {
  const environment = useEnvironmentOptional().data?.environment;
  const canConfigure = useCanConfigureObjects();
  const isRedacted = useIsRedacted();
  const { hasPermission } = usePermissions();

  return (
    <ObjectTypeResolver route="/workspaces/$workspaceId/objects/$slug">
      {(objectType, workspaceId) => (
        <ObjectTypeResolved
          objectType={objectType}
          workspaceId={workspaceId}
          environment={environment}
          canConfigure={canConfigure}
          canCreate={hasPermission('journeys/objects/create')}
          canDelete={hasPermission('journeys/objects/delete')}
          canExport={hasPermission('journeys/exports/create')}
          canCreateRelationships={hasPermission('journeys/object_relationships/create')}
          shouldRedactCreatedAt={isRedacted && environment?.is_sample_workspace !== true}
          canViewSettings={hasPermission('journeys/object_types/view')}
        />
      )}
    </ObjectTypeResolver>
  );
}

export default function ObjectTypePage() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <ObjectTypeContent />
    </Suspense>
  );
}
